import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Tooltip } from 'flowbite-react';
import { Change } from '../../models';

type AuthKey = 'spf' | 'dkim' | 'dmarc';
type IntakeHeaders = NonNullable<NonNullable<Change['intake']>['headers']>;

export type EmailAuthBadgesProps = {
    intake?: Change['intake'] | null;
    /** Direct headers (comment forensics or `intake.headers`). */
    headers?: IntakeHeaders | null;
    /** Compact badges for triage cards / dense tables. */
    compact?: boolean;
    /** Only show fail/softfail/error auth results and address mismatches. */
    failOnly?: boolean;
    className?: string;
};

const AUTH_KEYS: AuthKey[] = ['spf', 'dkim', 'dmarc'];

const isFailResult = (result?: string): boolean => {
    const normalized = String(result || '').toLowerCase();
    return normalized === 'fail'
        || normalized === 'softfail'
        || normalized === 'temperror'
        || normalized === 'permerror';
};

const badgeColorForResult = (result?: string): string => {
    const normalized = String(result || 'none').toLowerCase();
    if (normalized === 'pass')
        return 'success';
    if (normalized === 'fail' || normalized === 'softfail')
        return 'failure';
    if (normalized === 'temperror' || normalized === 'permerror')
        return 'warning';
    return 'gray';
};

/**
 * SPF / DKIM / DMARC (+ Reply-To / Return-Path mismatch) signals from intake headers.
 */
function EmailAuthBadges(props: EmailAuthBadgesProps) {
    const { intake, compact = false, failOnly = false, className = '' } = props;
    const { t } = useTranslation();
    const headers = props.headers || intake?.headers;
    if (!headers)
        return null;

    const auth = headers.auth || {};
    const authKeys = AUTH_KEYS.filter((key) => {
        if (!auth[key])
            return false;
        if (failOnly)
            return isFailResult(auth[key]);
        return true;
    });
    const showReplyToMismatch = Boolean(headers.replyToDiffersFrom);
    const showReturnPathMismatch = Boolean(headers.returnPathDiffersFrom);
    if (!authKeys.length && !showReplyToMismatch && !showReturnPathMismatch)
        return null;

    const size = compact ? 'xs' : 'sm';

    return (
        <div className={`flex flex-wrap items-center gap-1 ${className}`.trim()}>
            {authKeys.map((key) => {
                const result = auth[key] || 'none';
                const label = `${key.toUpperCase()} ${result}`;
                return (
                    <Tooltip
                        key={key}
                        content={t(`changes.email_auth_${key}_help`, { result }) as string}
                    >
                        <Badge color={badgeColorForResult(result)} size={size} className="w-fit uppercase">
                            {compact ? `${key.toUpperCase()}:${result}` : label}
                        </Badge>
                    </Tooltip>
                );
            })}
            {showReplyToMismatch && (
                <Tooltip content={t('changes.email_auth_reply_to_mismatch_help') as string}>
                    <Badge color="warning" size={size} className="w-fit">
                        {t('changes.email_auth_reply_to_mismatch')}
                    </Badge>
                </Tooltip>
            )}
            {showReturnPathMismatch && (
                <Tooltip content={t('changes.email_auth_return_path_mismatch_help') as string}>
                    <Badge color="warning" size={size} className="w-fit">
                        {t('changes.email_auth_return_path_mismatch')}
                    </Badge>
                </Tooltip>
            )}
        </div>
    );
}

export default EmailAuthBadges;
